/* Yerinde düzenleyicinin kabuğu — Görünüm ve Günlük aynı kabuğu kullanıyor.

   Üç sütun:

     sol    bölge seçici (varsa) ve belgenin ağacı
     orta   önizleme — sitenin kendi oluşturucusuyla, düzenleme kipinde.
            Metne tıklayıp yazılır; blokların üstünde ekle/çoğalt/taşı/sil
            çubuğu, seçili metnin üstünde biçim çubuğu (canvas.ts)
     sağ    seçili bloğun ayarları; seçim yoksa kaynağın kendi paneli
            (Görünüm'de tema, Günlük'te yazının bilgileri)

   Neyin düzenlendiğini bir `EditorSource` tarif ediyor: kök blok listesi,
   önizleme, kaydet, canlıya al, sürümler. Kabuk geri kalan her şeyi
   biliyor: geçmiş, şema denetimi, önizlemenin iki hızı, klavye.

   ── Belge ───────────────────────────────────────────────────

   Her değişiklik önce kaynağın şemasından geçiyor — geçmezse uygulanmıyor
   ve sebebi söyleniyor. Veritabanına yalnızca "Taslağı kaydet" ile, siteye
   yalnızca "Canlıya al" ile gidiliyor.

   ── Önizleme iki hızda ──────────────────────────────────────

   Metin yazılırken sayfa yeniden çizilmiyor (imleç yerinde kalmalı).
   Yalnızca CSS değiştiyse `<style id="fg-design">` değişiyor. Yapı değişince
   sayfa yeniden çiziliyor; kaydırma, seçim ve imleç geri konuyor. */

import type { Block } from '@frawgraphy/shared';
import { ApiError, publish } from '../api.ts';
import { el, replace } from '../dom.ts';
import type { AppContext } from '../main.ts';
import { attachCanvas, type Focus } from './canvas.ts';
import { blankBlock, TYPE_LABELS } from './blank.ts';
import { duplicateBlock, insertBlock, isTopLevel, itemOp, locate, moveBlock, moveIntoColumn, moveNextTo, removeBlock, setRich, takenIds } from './doc.ts';
import { renderInspector, type InspectorCtx } from './inspector.ts';
import { previewHtml, type Preview } from './page.ts';

export type Version = { id: number; kind: string; savedAt: string };

export type EditorSource = {
  title: string;
  /** Bölge seçici: Görünüm'de ana sayfa / works / blog / 404. */
  regions?: { key: string; label: string }[];
  region?(): string;
  setRegion?(key: string): void;
  /** Şu an düzenlenen kök liste. */
  root(): Block[];
  /** Şemadan geçmiş yeni kök listeyi yerleştirir; geçmezse mesaj döner. */
  apply(next: Block[]): string | null;
  preview(lang: 'tr' | 'en'): Preview;
  /** Geri al için bütün durumun (bütün bölgeler, tema, yazı bilgileri) anlık görüntüsü. */
  snapshot(): string;
  restore(snap: string): void;
  /** Kaydedilmiş / canlı hâllerin anlık görüntüsü — durum satırı için. */
  savedSnapshot(): string;
  liveSnapshot(): string | null;
  save(): Promise<void>;
  promote(): Promise<void>;
  versions?(): Promise<Version[]>;
  loadVersion?(id: number): Promise<void>;
  /** Seçim yokken sağ panel. */
  /**
   * Seçim yokken sağ panel. `change` panelden gelen bir değişikliği
   * uyguluyor: geri al geçmişine girer, önizleme ve durum yenilenir.
   */
  sidePanel(host: HTMLElement, ctx: InspectorCtx, change: (fn: () => void) => void): void;
  inspectorExtras?: Pick<InspectorCtx, 'theme' | 'tokens' | 'setTokens'>;
};

export function mountEditor(host: HTMLElement, ctx: AppContext, source: EditorSource, opts: { back?: () => void } = {}): void {
  const s = {
    selected: null as string | null,
    undo: [] as string[],
    redo: [] as string[],
    lastKey: '',
    lastAt: 0,
    device: 'desktop' as 'desktop' | 'mobile',
    theme: 'dark' as 'dark' | 'light',
    lang: 'tr' as 'tr' | 'en'
  };

  const status = el('span', { class: 'design-status' });
  const outline = el('ol', { class: 'design-tree' });
  const inspector = el('div', { class: 'design-inspector' });
  const frame = el('iframe', { class: 'design-frame', title: 'Sayfa önizlemesi' });
  const stage = el('div', { class: 'design-stage', 'data-device': s.device }, frame);

  const undoBtn = el('button', { type: 'button', text: '↶', title: 'Geri al (Ctrl+Z)', onclick: () => history(-1) });
  const redoBtn = el('button', { type: 'button', text: '↷', title: 'Yinele (Ctrl+Y)', onclick: () => history(1) });
  const saveBtn = el('button', { type: 'button', class: 'primary', text: 'Taslağı kaydet', onclick: () => void save() });
  const liveBtn = el('button', { type: 'button', text: 'Canlıya al', onclick: () => void goLive() });
  const toggle = (labels: [string, string], get: () => boolean, flip: () => void, title: string) => {
    const b = el('button', { type: 'button', text: get() ? labels[0] : labels[1], title });
    b.onclick = () => {
      flip();
      b.textContent = get() ? labels[0] : labels[1];
    };
    return b;
  };
  const langBtn = toggle(['TR', 'EN'], () => s.lang === 'tr', () => {
    canvas.flush();
    s.lang = s.lang === 'tr' ? 'en' : 'tr';
    draw(true);
  }, 'Düzenlenen dil');
  const deviceBtn = toggle(['Masaüstü', 'Telefon'], () => s.device === 'desktop', () => {
    s.device = s.device === 'desktop' ? 'mobile' : 'desktop';
    stage.dataset['device'] = s.device;
  }, 'Önizleme genişliği');
  const themeBtn = toggle(['Koyu', 'Açık'], () => s.theme === 'dark', () => {
    s.theme = s.theme === 'dark' ? 'light' : 'dark';
    frame.contentDocument?.documentElement.setAttribute('data-theme', s.theme);
    drawInspector();
  }, 'Önizlemenin teması');

  const regionPicker = source.regions
    ? (() => {
        const select = el(
          'select',
          { 'aria-label': 'Düzenlenen sayfa' },
          ...source.regions.map((r) => el('option', { value: r.key, text: r.label, selected: r.key === source.region?.() }))
        );
        select.addEventListener('change', () => {
          canvas.flush();
          source.setRegion?.(select.value);
          s.selected = null;
          refresh();
          draw(true);
        });
        return el('label', { class: 'design-add' }, select);
      })()
    : null;

  replace(
    host,
    el(
      'div',
      { class: 'row design-bar' },
      opts.back ? el('button', { type: 'button', text: '←', title: 'Listeye dön', onclick: () => leave() }) : null,
      el('h1', { text: source.title, style: 'margin:0' }),
      status,
      el('span', { class: 'spacer' }),
      undoBtn,
      redoBtn,
      langBtn,
      deviceBtn,
      themeBtn,
      source.versions ? el('button', { type: 'button', text: 'Sürümler', onclick: () => void showVersions() }) : null,
      saveBtn,
      liveBtn
    ),
    el(
      'div',
      { class: 'design-grid' },
      el(
        'aside',
        { class: 'design-side' },
        regionPicker,
        outline,
        el('button', { type: 'button', class: 'design-theme-link', text: 'Seçimi kaldır — genel ayarlar', onclick: () => select(null) })
      ),
      stage,
      inspector
    )
  );

  /* ── Değişiklik ── */

  function remember(key: string): void {
    const now = Date.now();
    if (!(key && key === s.lastKey && now - s.lastAt < 1500)) s.undo.push(source.snapshot());
    if (s.undo.length > 200) s.undo.shift();
    s.redo = [];
    s.lastKey = key;
    s.lastAt = now;
  }

  function mutate(fn: (root: Block[]) => string | null | void, after: { focus?: Focus; select?: string | null } = {}): void {
    canvas.flush();
    const before = source.snapshot();
    const next = structuredClone(source.root());
    const result = fn(next);
    const error = source.apply(next);
    if (error) {
      ctx.toast(error, 'bad');
      return;
    }
    if (source.snapshot() !== before) {
      s.undo.push(before);
      s.redo = [];
      s.lastKey = '';
    }
    if (after.select !== undefined) s.selected = after.select;
    else if (typeof result === 'string') s.selected = result;
    if (s.selected && !locate(source.root(), s.selected)) s.selected = null;
    canvas.focusAfterRender(after.focus);
    refresh();
  }

  function commitText(blockId: string, path: string, html: string): void {
    const next = structuredClone(source.root());
    const loc = locate(next, blockId);
    if (!loc) return;
    setRich(loc.block, path, s.lang, html);
    const before = source.snapshot();
    const prevKey = s.lastKey;
    const prevAt = s.lastAt;
    const error = source.apply(next);
    if (error) {
      ctx.toast(error, 'bad');
      return;
    }
    if (source.snapshot() === before) return;
    /* Aynı alana art arda yazmak tek geri alma adımı. */
    const key = `${blockId}.${path}.${s.lang}`;
    const now = Date.now();
    if (!(key === prevKey && now - prevAt < 1500)) s.undo.push(before);
    s.redo = [];
    s.lastKey = key;
    s.lastAt = now;
    drawStatus();
    drawOutline();
  }

  /** Kaynağın kendi panelinden gelen değişiklik (tema, yazı bilgileri). */
  function external(change: () => void): void {
    remember('external');
    change();
    draw(false);
    drawStatus();
  }

  function history(step: -1 | 1): void {
    canvas.flush();
    const from = step < 0 ? s.undo : s.redo;
    const to = step < 0 ? s.redo : s.undo;
    const snap = from.pop();
    if (!snap) return;
    to.push(source.snapshot());
    source.restore(snap);
    s.lastKey = '';
    if (s.selected && !locate(source.root(), s.selected)) s.selected = null;
    refresh();
  }

  function select(id: string | null): void {
    s.selected = id;
    canvas.refreshSelection();
    drawOutline();
    drawInspector();
  }

  function insert(type: Parameters<typeof blankBlock>[0], afterId: string | null, replaceId?: string): void {
    let focus: Focus | undefined;
    mutate((root) => {
      const block = blankBlock(type, takenIds(root));
      const ok = afterId ? insertBlock(root, block, { afterId }) : isTopLevel(block) ? insertBlock(root, block, {}) : insertBlock(root, block, { into: lastContainer(root) });
      if (!ok) return null;
      if (replaceId) removeBlock(root, replaceId);
      if ('text' in block) focus = { blockId: block.id, path: 'text', at: 'end' };
      return block.id;
    });
    if (focus) canvas.focusAfterRender(focus);
  }

  function lastContainer(root: Block[]): Block[] {
    const last = [...root].reverse().find((b) => b.type === 'section' || b.type === 'page');
    return last && (last.type === 'section' || last.type === 'page') ? last.blocks : root;
  }

  function refresh(): void {
    draw(false);
    drawOutline();
    drawInspector();
    drawStatus();
  }

  /* ── Çizim ── */

  function drawStatus(): void {
    const now = source.snapshot();
    const dirty = now !== source.savedSnapshot();
    const offLive = now !== source.liveSnapshot();
    status.textContent = dirty ? '● kaydedilmemiş değişiklik' : offLive ? 'taslak kaydedildi · canlıdan farklı' : 'canlıyla aynı';
    status.className = dirty ? 'design-status bad' : 'design-status dim';
    undoBtn.disabled = s.undo.length === 0;
    redoBtn.disabled = s.redo.length === 0;
    saveBtn.disabled = !dirty;
  }

  const inspectorCtx = (): InspectorCtx => ({
    home: () => source.root(),
    mutate,
    select,
    toast: ctx.toast,
    fail: ctx.fail,
    theme: () => s.theme,
    tokens: source.inspectorExtras?.tokens ?? (() => ({ dark: {}, light: {} })),
    setTokens: (fn) => external(() => source.inspectorExtras?.setTokens(fn))
  });

  function drawInspector(): void {
    if (s.selected && locate(source.root(), s.selected)) {
      renderInspector(inspector, s.selected, inspectorCtx());
    } else {
      source.sidePanel(inspector, inspectorCtx(), (fn) => {
        external(fn);
        drawOutline();
      });
    }
  }

  function drawOutline(): void {
    const item = (b: Block, depth: number): HTMLElement[] => {
      const label = labelOf(b);
      const row = el(
        'li',
        {
          'data-depth': depth,
          'aria-current': b.id === s.selected ? 'true' : null,
          'data-hidden': b.style?.hidden ? '' : null,
          onclick: (e: Event) => {
            e.stopPropagation();
            select(b.id);
            const node = frame.contentDocument?.querySelector(`[data-b="${CSS.escape(b.id)}"]`) as HTMLElement | null;
            if (node && frame.contentWindow) frame.contentWindow.scrollTo({ top: node.getBoundingClientRect().top + frame.contentWindow.scrollY - 60, behavior: 'smooth' });
          }
        },
        el('span', { class: 'design-tree-type', text: TYPE_LABELS[b.type] }),
        label ? el('span', { class: 'design-tree-text', text: label }) : null
      );
      const kids = b.type === 'section' || b.type === 'page' ? [...b.head, ...b.blocks] : b.type === 'columns' ? b.columns.flatMap((c) => c.blocks) : b.type === 'split' ? b.blocks : [];
      return [row, ...kids.flatMap((k) => item(k, depth + 1))];
    };
    replace(outline, ...source.root().flatMap((b) => item(b, 0)));
  }

  /* ── Önizleme ── */

  let lastBody = '';
  let drawTimer: ReturnType<typeof setTimeout> | undefined;

  function draw(force: boolean): void {
    clearTimeout(drawTimer);
    drawTimer = setTimeout(() => {
      const p = source.preview(s.lang);
      const styleTag = frame.contentDocument?.getElementById('fg-design');
      if (!force && p.body === lastBody && styleTag) {
        styleTag.textContent = p.css;
        return;
      }
      lastBody = p.body;
      const scrollY = frame.contentWindow?.scrollY ?? 0;
      try {
        /* Önizleme panelle aynı kökende: sitenin dil tercihi panelin
           depolamasından okunuyor. EN düzenlerken sayfa İngilizce açılsın. */
        localStorage.setItem('frawgraphy-lang', s.lang);
      } catch {
        /* özel pencere — önizleme TR açılır */
      }
      frame.onload = () => {
        frame.contentDocument?.documentElement.setAttribute('data-theme', s.theme);
        frame.contentWindow?.scrollTo(0, scrollY);
        frame.contentDocument?.addEventListener('keydown', onKey);
        canvas.onLoad();
      };
      frame.srcdoc = previewHtml(p);
    }, force ? 0 : 60);
  }

  const canvas = attachCanvas({
    frame,
    home: () => source.root(),
    lang: () => s.lang,
    selected: () => s.selected,
    select,
    commitText,
    mutate,
    insert,
    itemOp: (blockId, itemId, op) => mutate((root) => (itemOp(root, blockId, itemId, op) ? blockId : null)),
    confirmRemove: (block) => {
      if (block.type === 'contact') return confirm('Bu blok sitedeki tek proje formunu taşıyor. Yine de silinsin mi?');
      if (block.type === 'section' || block.type === 'hero') return confirm(`"${labelOf(block) || TYPE_LABELS[block.type]}" ve içindeki her şey silinecek. Geri almak için ↶. Devam?`);
      return true;
    }
  });
  canvas.bindOps({
    duplicate: (root, id) => duplicateBlock(root, id),
    move: (root, id, step) => (moveBlock(root, id, step) ? id : null),
    dragDrop: (root, id, target, after) => (target.startsWith('col:') ? moveIntoColumn(root, id, target.slice(4)) : moveNextTo(root, id, target, after))
  });

  /* ── Kayıt ve yayın ── */

  async function save(): Promise<boolean> {
    canvas.flush();
    saveBtn.disabled = true;
    try {
      await source.save();
      ctx.toast('Taslak kaydedildi.');
      return true;
    } catch (err) {
      ctx.fail(err);
      return false;
    } finally {
      drawStatus();
    }
  }

  async function goLive(): Promise<void> {
    canvas.flush();
    if (source.snapshot() !== source.savedSnapshot() && !(await save())) return;
    if (!confirm('Taslak canlıya alınacak ve site yeniden üretilecek. Devam edilsin mi?')) return;
    liveBtn.disabled = true;
    try {
      await source.promote();
      drawStatus();
      try {
        await publish.run();
        ctx.toast('Canlıya alındı — site yeniden üretiliyor, birkaç dakika sürer.');
      } catch (err) {
        const why = err instanceof ApiError ? err.message : 'yayın başlatılamadı';
        ctx.toast(`Canlıya kaydedildi ama site henüz üretilmedi (${why}). Yayın ekranından yeniden deneyin.`, 'bad');
      }
    } catch (err) {
      ctx.fail(err);
    } finally {
      liveBtn.disabled = false;
    }
  }

  async function showVersions(): Promise<void> {
    if (!source.versions || !source.loadVersion) return;
    let rows: Version[];
    try {
      rows = await source.versions();
    } catch (err) {
      ctx.fail(err);
      return;
    }
    replace(
      inspector,
      el('p', { class: 'tile-name', text: 'Sürümler' }),
      el('p', { class: 'dim', text: 'Bir sürümü editöre yüklemek siteyi değiştirmez; bakıp kaydedebilir ya da ↶ ile geri alabilirsiniz.' }),
      rows.length === 0
        ? el('p', { class: 'dim', text: 'Henüz kayıt yok.' })
        : el(
            'table',
            {},
            el(
              'tbody',
              {},
              ...rows.map((v) =>
                el(
                  'tr',
                  {},
                  el('td', { text: new Date(v.savedAt).toLocaleString('tr-TR') }),
                  el('td', { class: v.kind === 'live' ? 'ok' : 'dim', text: v.kind === 'live' ? 'canlı' : 'taslak' }),
                  el(
                    'td',
                    {},
                    el('button', {
                      type: 'button',
                      text: 'Yükle',
                      onclick: async () => {
                        try {
                          remember('');
                          await source.loadVersion!(v.id);
                          s.selected = null;
                          refresh();
                          draw(true);
                          ctx.toast('Sürüm editöre yüklendi — kaydetmeden siteye gitmez.');
                        } catch (err) {
                          ctx.fail(err);
                        }
                      }
                    })
                  )
                )
              )
            )
          ),
      el('button', { type: 'button', text: '← Ayarlara dön', onclick: () => drawInspector() })
    );
  }

  /* ── Klavye ve çıkış ── */

  function onKey(e: KeyboardEvent): void {
    if (!document.contains(host)) return;
    const mod = e.ctrlKey || e.metaKey;
    if (!mod) return;
    const key = e.key.toLowerCase();
    if (key === 's') {
      e.preventDefault();
      void save();
      return;
    }
    /* Yazarken Ctrl+Z alanın kendi geri almasıdır; dışındayken belgenin. */
    const tag = (e.target as HTMLElement | null)?.tagName;
    if (canvas.isEditing() || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    if (key === 'z') {
      e.preventDefault();
      history(e.shiftKey ? 1 : -1);
    } else if (key === 'y') {
      e.preventDefault();
      history(1);
    }
  }
  document.addEventListener('keydown', onKey);
  window.onbeforeunload = () => (source.snapshot() !== source.savedSnapshot() ? 'Kaydedilmemiş değişiklik var.' : undefined);

  function leave(): void {
    canvas.flush();
    if (source.snapshot() !== source.savedSnapshot() && !confirm('Kaydedilmemiş değişiklik var. Yine de çıkılsın mı?')) return;
    document.removeEventListener('keydown', onKey);
    window.onbeforeunload = null;
    opts.back?.();
  }

  refresh();
  draw(true);
}

/** Ağaçta ve onay kutularında görünen kısa metin. */
export function labelOf(b: Block): string {
  const strip = (h: string) => h.replace(/<[^>]+>/g, '').replace(/&amp;/g, '&').replace(/&nbsp;/g, ' ').trim().slice(0, 40);
  if (b.type === 'section' || b.type === 'page') {
    const title = b.head.find((x) => x.type === 'heading');
    return title && 'text' in title ? strip(title.text.tr) : b.type === 'section' ? `#${b.anchor}` : '';
  }
  if ('text' in b && b.text) return strip(b.text.tr);
  if (b.type === 'hero') return b.lines.map((l) => l.text).join(' ');
  if (b.type === 'nav') return `${b.items.length} bağlantı`;
  if (b.type === 'footer') return `${b.links.length} bağlantı`;
  if (b.type === 'cards' || b.type === 'reels' || b.type === 'facts' || b.type === 'gallery') return `${b.items.length} öğe`;
  return '';
}
